import { hasRealPrice } from 'utils/price';
import { useEffect, useRef, useState } from 'react';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { defaultStagedFinish, finishFields, stagedFinishSource } from 'utils/finishes';
import { getValidCondition, stagedPrintingFromRow } from 'utils/wantListHelpers';

/**
 * Batched display-price fill for want-list rows without a displayPrice, the
 * same pattern the profile Want List tab uses (fetched-ids ref so each entry
 * is requested once). These prices are informational only — they never seed
 * an offer; addable rows always carry quotedPricePoints from offer-candidates.
 */
export const useWantListPriceFill = (
  rows: CollectionList[] | undefined
): Record<number, number> => {
  const [priceMap, setPriceMap] = useState<Record<number, number>>({});
  const fetchedIdsRef = useRef<Set<number>>(new Set());
  const [calculatePrice] = useCalculatePriceMutation();

  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const unpriced = rows.filter(
      (item) => item.displayPrice == null && !fetchedIdsRef.current.has(item.id)
    );
    unpriced.forEach((item) => fetchedIdsRef.current.add(item.id));
    // A row with no sendable version has no price to fetch: it stays unfilled
    const itemsNeedingPrice = unpriced.flatMap((item) => {
      const staged = stagedPrintingFromRow(item, item.card?.id);
      return staged ? [{ item, staged }] : [];
    });
    if (itemsNeedingPrice.length === 0) return;

    const fetchPrices = async () => {
      try {
        const result = await calculatePrice({
          cards: itemsNeedingPrice.map(({ item, staged }) => {
            let setDetails = item.acceptableSetDetails;
            if (!setDetails || setDetails.length === 0) {
              setDetails = item.acceptableSets?.map((setName, idx) => ({
                name: setName,
                code:
                  item.acceptableSetCodes?.[idx] ||
                  setName.toLowerCase().replace(/\s+/g, ''),
                cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
              }));
            }
            const matchingSetDetail =
              setDetails?.find((s) => s.cardId === item.card?.id) || setDetails?.[0];
            const defaultFinish = defaultStagedFinish(
              stagedFinishSource(matchingSetDetail, item.card),
              item
            );
            return {
              wantListId: item.id,
              cardId: staged.cardId?.toString(),
              condition: getValidCondition(item.acceptableConditions?.[0]),
              language: staged.language,
              ...finishFields(defaultFinish),
              set: item.card?.setName,
            };
          }),
        }).unwrap();

        const newPrices: Record<number, number> = {};
        result.cards.forEach((card) => {
          // A null or non-positive slot is no price: leave the row unfilled
          if (hasRealPrice(card.displayPrice)) newPrices[card.wantListId] = card.displayPrice;
        });
        setPriceMap((prev) => ({ ...prev, ...newPrices }));
      } catch (error) {
        // Missing prices render the existing "No price data" state — never block
        console.error('Failed to calculate want-list display prices:', error);
      }
    };
    fetchPrices();
  }, [rows, calculatePrice]);

  return priceMap;
};
